import { useEffect, useRef } from "react";

const IMG = "/img/fifa";

const historyImages = [
  { mobile: `${IMG}/history-1-mobile.jpg`, desktop: `${IMG}/history-1-desktop.jpg`, alt: "Stand de Kia con minijuego de fútbol", span: "col-span-2 md:col-span-2", delay: 300 },
  { mobile: `${IMG}/history-2-mobile.png`, desktop: `${IMG}/history-2-desktop.jpg`, alt: "Sam Kerr, jugadora de la selección australiana", span: "col-span-1", delay: 420 },
  { mobile: `${IMG}/history-3-mobile.png`, desktop: `${IMG}/history-3-desktop.jpg`, alt: "Estadio con publicidad de Kia EV9", span: "col-span-1", delay: 540 },
  { mobile: `${IMG}/history-4-mobile.png`, desktop: `${IMG}/history-4-desktop.jpg`, alt: "Aficionados con merchandising de Kia", span: "col-span-2 md:col-span-2", delay: 660 },
  { mobile: `${IMG}/history-5-mobile.png`, desktop: `${IMG}/history-5-desktop.jpg`, alt: "Vehículo eléctrico Kia en estadio del Mundial de Catar", span: "col-span-1", delay: 780 },
  { mobile: `${IMG}/history-6-mobile.png`, desktop: `${IMG}/history-6-desktop.jpg`, alt: "Kia EV9 en espacio de exhibición", span: "col-span-1", delay: 900 },
  { mobile: `${IMG}/history-7-mobile.png`, desktop: `${IMG}/history-7-desktop.jpg`, alt: "Activación de marca Kia en evento FIFA", span: "col-span-1", delay: 1020 },
];

const activities = [
  {
    title: "Soporte oficial de vehículos",
    mobile: `${IMG}/activity-1-mobile.jpg`,
    desktop: `${IMG}/activity-1-desktop.jpg`,
    alt: "Kia Carnival, vehículo oficial FIFA World Cup en hotel",
    body: "A lo largo de los torneos internacionales de la FIFA, Kia ha sido el socio oficial de movilidad, garantizando desplazamientos ágiles y eficientes para jugadores y oficiales. Incluso detrás de escena, Kia mantiene el juego en movimiento, apoyando los momentos que dan forma al fútbol en el escenario más grande del mundo.",
  },
  {
    title: "Programas Juveniles y de Nuevas Generaciones",
    mobile: `${IMG}/activity-2-mobile.jpg`,
    desktop: `${IMG}/activity-2-desktop.jpg`,
    alt: "Niño recogebalones ingresando al campo con balón de fútbol",
    body: "Kia continúa apoyando los diversos escenarios del fútbol impulsados por la FIFA, desde torneos juveniles y femeninos hasta futsal y la eWorld Cup, donde se construye el futuro del deporte. A través de este compromiso, el fútbol se convierte en una plataforma de inclusión, reforzando el valor de un deporte abierto para todos.",
  },
  {
    title: "FIFA Fan Festival",
    mobile: `${IMG}/activity-3-mobile.jpg`,
    desktop: `${IMG}/activity-3-desktop.jpg`,
    alt: "Aficionado en zona de experiencia Kia durante partido",
    body: "El FIFA Fan Festival es un espacio de celebración donde los aficionados se reúnen más allá del estadio. En el festival, Kia ofrece experiencias centradas en la movilidad que dan vida a la emoción y la cultura del fútbol.",
  },
  {
    title: "Stand de Marca",
    mobile: `${IMG}/activity-4-mobile.jpg`,
    desktop: `${IMG}/activity-4-desktop.jpg`,
    alt: "Stand de experiencia de marca Kia con pantalla digital",
    body: "El stand de marca de Kia es un espacio de interacción significativa con los aficionados, donde el futuro de la movilidad cobra vida. A través de su portafolio de vehículos eléctricos, Kia expresa claramente su identidad como marca electrificada y su visión de movilidad sostenible.",
  },
];

export default function PatrocinadorFifa() {
  const stadiumSectionRef = useRef(null);
  const heroTaglineRef = useRef(null);

  useEffect(() => {
    document.title = "Kia, patrocinador oficial de la FIFA World Cup 2026™";
  }, []);

  // IntersectionObserver — reveal-on-scroll for [data-reveal]
  useEffect(() => {
    const els = document.querySelectorAll(".fifa-page [data-reveal]");
    els.forEach((el) => {
      const delay = el.dataset.revealDelay;
      if (delay) el.style.setProperty("--reveal-delay", `${delay}ms`);
    });
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-revealed");
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2, rootMargin: "0px 0px -10% 0px" }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  // Hero tagline reveal on mount
  useEffect(() => {
    const t = setTimeout(() => {
      heroTaglineRef.current?.classList.add("is-revealed");
    }, 100);
    return () => clearTimeout(t);
  }, []);

  // Stadium section — scroll-driven stage transitions (copy → logo)
  useEffect(() => {
    const section = stadiumSectionRef.current;
    if (!section) return;
    const overlay = section.querySelector("[data-reason-overlay]");
    const copyStage = section.querySelector('[data-reason-stage="copy"]');
    const logoStage = section.querySelector('[data-reason-stage="logo"]');
    const COPY_THRESHOLD = 0.25;
    const LOGO_THRESHOLD = 0.6;
    let queued = false;
    let lastStage = "";

    const update = () => {
      queued = false;
      const rect = section.getBoundingClientRect();
      const scrollable = rect.height - window.innerHeight;
      if (scrollable <= 0) return;
      const progress = Math.max(0, Math.min(1, -rect.top / scrollable));
      let stage = "idle";
      if (progress >= LOGO_THRESHOLD) stage = "logo";
      else if (progress >= COPY_THRESHOLD) stage = "copy";
      if (stage === lastStage) return;
      lastStage = stage;
      if (overlay) {
        overlay.classList.toggle("bg-[#05141F]/60", stage !== "idle");
        overlay.classList.toggle("bg-[#05141F]/10", stage === "idle");
      }
      copyStage?.classList.toggle("is-active", stage === "copy");
      logoStage?.classList.toggle("is-active", stage === "logo");
    };
    const onScroll = () => {
      if (!queued) {
        queued = true;
        requestAnimationFrame(update);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    update();
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  return (
    <div className="fifa-page font-kia bg-[#05141F] text-white">
      {/* HERO */}
      <section className="relative h-screen w-full overflow-hidden bg-[#05141F]">
        <video
          className="absolute inset-0 h-full w-full object-cover"
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
        >
          <source src="https://cdn.kia.com.co/fifa_hero_desktop_d82c64f262.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-black/30" />
        <div className="relative z-10 flex h-full w-full items-start p-6 md:p-12 pt-[80px] md:pt-[140px]">
          <h1 className="text-white font-bold text-base md:text-lg tracking-wide">FIFA</h1>
        </div>
        <div
          ref={heroTaglineRef}
          className="fifa-hero-tagline pointer-events-none absolute inset-0 z-20 flex items-end md:items-center justify-center px-6 md:px-12 pb-16 md:pb-0"
        >
          <div className="max-w-2xl text-center">
            <p className="text-white text-3xl md:text-5xl font-bold leading-tight drop-shadow-lg">
              FIFA World Cup 2026™
            </p>
            <p className="text-white/90 mt-4 text-sm md:text-base leading-relaxed drop-shadow">
              El movimiento de Kia se une a la pasión de la FIFA
            </p>
          </div>
        </div>
      </section>

      {/* REASON WHY — sticky stadium with stage transitions */}
      <section ref={stadiumSectionRef} className="relative">
        <div className="relative h-[300vh] md:h-[320vh]">
          <div className="sticky top-0 h-screen overflow-hidden">
            <picture>
              <source media="(min-width: 768px)" srcSet={`${IMG}/stadium-desktop.jpg`} />
              <img
                src={`${IMG}/stadium-mobile.jpg`}
                alt="Estadio con vallas de marca Kia"
                className="absolute inset-0 h-full w-full object-cover"
                loading="lazy"
                width="1920"
                height="1080"
              />
            </picture>
            <div
              data-reason-overlay
              className="absolute inset-0 transition-colors duration-700 bg-[#05141F]/10"
            />
            <div
              data-reason-stage="copy"
              className="absolute inset-0 flex items-center justify-center px-6 md:px-16"
            >
              <div className="max-w-3xl text-center">
                <h2
                  data-stage-step="1"
                  className="text-2xl md:text-4xl font-bold text-white drop-shadow-lg"
                >
                  ¿Por qué Kia se asocia con FIFA?
                </h2>
                <p
                  data-stage-step="2"
                  className="mt-6 text-sm md:text-base leading-relaxed text-white/90 drop-shadow"
                >
                  La FIFA ha creado el escenario más poderoso del mundo, reuniendo a las personas a
                  través del fútbol. Más allá de fronteras e idiomas, el fútbol se ha convertido en
                  una cultura global, profundamente integrada en la vida cotidiana alrededor del
                  mundo.
                </p>
                <p
                  data-stage-step="3"
                  className="mt-4 text-sm md:text-base leading-relaxed text-white/90 drop-shadow"
                >
                  Esta influencia global se alinea estrechamente con los valores de Kia: conectar a
                  las personas y al mundo a través del movimiento. A través de su asociación con la
                  FIFA, Kia continúa creando momentos significativos que inspiran conexión y
                  participación.
                </p>
              </div>
            </div>
            <div
              data-reason-stage="logo"
              className="absolute inset-0 flex items-center justify-center px-6"
            >
              <img
                src={`${IMG}/fifa-kia-logo.png`}
                alt="FIFA Official Partner — Kia"
                className="w-3/4 max-w-md md:max-w-lg"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* HISTORY */}
      <section className="relative z-10 -mt-12 rounded-t-3xl bg-[#05141F] px-6 py-20 md:-mt-20 md:py-32 md:px-12 md:rounded-t-[2.5rem]">
        <div className="mx-auto max-w-5xl">
          <h2 data-reveal className="text-3xl md:text-5xl font-bold text-white">
            Un largo recorrido junto a la FIFA
          </h2>
          <div className="mt-6 max-w-3xl space-y-4 text-sm md:text-base text-white/80 leading-relaxed">
            <p data-reveal data-reveal-delay="120">
              Desde 2010, como socio oficial de la FIFA, Kia ha acompañado la pasión de los
              aficionados al fútbol en todo el mundo. Para ayudar a que esta celebración global
              llegue a más personas, Kia ha apoyado las operaciones del torneo y la movilidad de
              manera eficiente y responsable.
            </p>
            <p data-reveal data-reveal-delay="200">
              Sobre la base de esta experiencia, Kia continúa trabajando junto a la FIFA para
              contribuir a dar forma al futuro de la movilidad sostenible.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-2 gap-3 md:mt-16 md:grid-cols-4 md:gap-4">
            {historyImages.map((h, i) => (
              <div
                key={i}
                data-reveal
                data-reveal-delay={h.delay}
                className={`overflow-hidden bg-[#34373A] ${h.span}`}
              >
                <picture>
                  <source media="(min-width: 768px)" srcSet={h.desktop} />
                  <img
                    src={h.mobile}
                    alt={h.alt}
                    className="h-full w-full object-cover"
                    loading="lazy"
                    width="800"
                    height="600"
                  />
                </picture>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SPORTAGE CTA */}
      <section className="relative bg-white px-6 py-20 md:py-32 md:px-12">
        <div className="mx-auto max-w-6xl grid gap-10 md:grid-cols-2 md:items-center md:gap-16">
          <div data-reveal className="aspect-square w-full overflow-hidden bg-[#F5F5F5]">
            <img
              src={`${IMG}/sportage-new.jpg`}
              alt="Kia Sportage"
              className="h-full w-full object-cover"
              loading="lazy"
            />
          </div>
          <div className="text-[#05141F]">
            <h2 data-reveal className="text-2xl md:text-4xl font-bold leading-tight">
              Kia Sportage:<br />El espacio donde todos conectan
            </h2>
            <div className="mt-6 space-y-4 text-sm md:text-base text-[#697279] leading-relaxed">
              <p data-reveal data-reveal-delay="120">
                Kia Sportage refleja la evolución del SUV moderno a través de un diseño expresivo,
                tecnología centrada en las personas y una experiencia de conducción equilibrada.
                Como parte de la visión de movilidad de Kia, integra innovación, confort y
                funcionalidad para acompañar distintos estilos de vida de manera consistente y
                responsable.
              </p>
            </div>
            <a
              href="https://www.kia.com.ar/sportage"
              target="_blank"
              rel="noopener noreferrer"
              data-reveal
              data-reveal-delay="320"
              className="mt-8 inline-flex items-center justify-center border border-[#05141F] bg-[#05141F] px-8 py-3 text-sm font-bold text-white transition-colors hover:bg-white hover:text-[#05141F]"
            >
              Conocé más
            </a>
          </div>
        </div>
      </section>

      {/* ACTIVITIES */}
      <section className="bg-[#05141F] px-6 py-20 md:py-32 md:px-12">
        <div className="mx-auto max-w-6xl">
          <h2 data-reveal className="text-3xl md:text-5xl font-bold text-white">
            Momentos que inspiran, impulsados por Kia
          </h2>
          <p data-reveal data-reveal-delay="120" className="mt-4 text-base md:text-lg text-white/80">
            Apoyando el fútbol para todos.
          </p>
          <div className="mt-12 grid gap-10 md:mt-16 md:grid-cols-2 md:gap-12">
            {activities.map((a, i) => (
              <article key={i} className="flex flex-col">
                <h3 data-reveal className="text-xl md:text-2xl font-bold text-white">
                  {a.title}
                </h3>
                <div
                  data-reveal
                  data-reveal-delay="180"
                  className="mt-5 aspect-[4/3] w-full overflow-hidden bg-[#34373A]"
                >
                  <picture>
                    <source media="(min-width: 768px)" srcSet={a.desktop} />
                    <img
                      src={a.mobile}
                      alt={a.alt}
                      className="h-full w-full object-cover"
                      loading="lazy"
                      width="800"
                      height="600"
                    />
                  </picture>
                </div>
                <p
                  data-reveal
                  data-reveal-delay="360"
                  className="mt-5 text-sm md:text-base text-white/80 leading-relaxed"
                >
                  {a.body}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* OMBC */}
      <section className="relative overflow-hidden bg-[#05141F]">
        <picture>
          <source media="(min-width: 768px)" srcSet={`${IMG}/ombc-desktop.jpg`} />
          <img
            src={`${IMG}/ombc-mobile.jpg`}
            alt="Niño recogebalones con uniforme Kia ingresando al estadio"
            className="h-[60vh] w-full object-cover md:h-[70vh]"
            loading="lazy"
            width="1920"
            height="1080"
            data-reveal
            data-reveal-pop=""
          />
        </picture>
        <div className="absolute inset-0 bg-gradient-to-t from-[#05141F]/80 via-[#05141F]/40 to-transparent" />
        <div className="absolute inset-0 flex items-end justify-center px-6 pb-12 md:items-center md:pb-0 md:px-16">
          <div className="max-w-2xl text-center">
            <h2 data-reveal className="text-2xl md:text-4xl font-bold text-white drop-shadow-lg">
              Las leyendas del mañana saltan a la cancha: OMBC
            </h2>
            <p
              data-reveal
              data-reveal-delay="120"
              className="mt-4 text-sm md:text-base text-white/90 leading-relaxed drop-shadow"
            >
              Los Official Match Ball Carriers (OMBC) son niños y niñas que sueñan con convertirse
              en futuras estrellas del fútbol. Al ingresar al campo junto a los jugadores y
              entregar el balón oficial del partido al árbitro, el próximo capítulo del fútbol —y
              la inspiración— comienza en sus manos.
            </p>
            <a
              href="https://org-worldwide.kia.com/en/brand/brand-stories/official-match-ball-carrier/"
              target="_blank"
              rel="noopener noreferrer"
              data-reveal
              data-reveal-delay="240"
              className="mt-8 inline-flex items-center justify-center border border-white bg-transparent px-8 py-3 text-sm font-bold text-white transition-colors hover:bg-white hover:text-[#05141F]"
            >
              Conocé más
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
